import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class CdShippingProgress extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #handleCartUpdate = async (event) => {
    let total = event?.detail?.resource?.total_price;

    // CartAddEvent is dispatched on the same channel but sometimes with an empty resource,
    // so fall back to /cart.js when total_price isn't on the payload.
    if (typeof total !== 'number') {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        total = cart.total_price;
      } catch {
        return;
      }
    }

    this.#render(total);
  };

  #render(cartTotal) {
    const minTotal = Number(this.dataset.minTotal) || 0;
    if (minTotal <= 0) return;

    const ratio = Math.min(cartTotal / minTotal, 1);
    const met = cartTotal >= minTotal;

    const fill = this.querySelector('[data-cd-progress-fill]');
    const message = this.querySelector('[data-cd-progress-message]');
    const track = this.querySelector('[role="progressbar"]');

    if (fill) fill.style.setProperty('--cd-progress', String(ratio));
    if (track) track.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));

    this.toggleAttribute('data-threshold-met', met);

    // Mirror legacy Archetype Impulse behavior: toggle `threshold-met` on the cart form
    // so other CSS can react to it. Re-queried each render — Horizon morphs the cart
    // section on update, so the form node may be replaced.
    const cartForm = document.getElementById('cart-form');
    cartForm?.classList.toggle('threshold-met', met);

    if (!message) return;

    if (met) {
      message.textContent = this.dataset.messageComplete || '';
      return;
    }

    const remaining = minTotal - cartTotal;
    const formatted = formatMoney(
      remaining,
      this.dataset.moneyFormat || '${{amount}}',
      this.dataset.currency || 'USD'
    );
    const template = this.dataset.messageIncomplete || '';
    message.innerHTML = template.replaceAll('{$amount}', formatted);
  }
}

if (!customElements.get('cd-shipping-progress')) {
  customElements.define('cd-shipping-progress', CdShippingProgress);
}
